import React, { useEffect, useState } from 'react';
import { Info, ExternalLink, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { SEO } from '../components/SEO';
import {
  fetchReadingSchedule,
  ReadingItem,
  fetchReadingPageContent,
  ReadingPageContent,
} from '../lib/readingSchedule';

const defaultPageContent: ReadingPageContent = {
  header_title: 'Gospel of John',
  header_description:
    'Discovering the person, power, and purpose of Jesus Christ through the Gospel of John.',
  footer_title: 'About the Gospel of John',
  footer_description:
    "The Gospel of John offers a unique perspective on the life and ministry of Jesus Christ. Traditionally attributed to John the Apostle, this Gospel emphasizes Jesus' divine nature and His identity as the Son of God.\n\nAs we journey through John's account together, we'll encounter profound teachings, miraculous signs, and powerful conversations that reveal who Jesus is and what it means to follow Him. Throughout the Gospel, we'll see the depth of Christ's love and His invitation to experience true life through faith in Him.",
};

// Only ever render links that point at a real web address. Anything else
// (for example a `javascript:` address) is dropped rather than linked.
const safeUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
};

interface ScheduleItem {
  date: string;
  readingDate?: string;
  location: string;
  text: string;
  readingPrompt?: string;
  month: string;
  isOff?: boolean;
  offReason?: string;
  esvUrl?: string;
  podcastUrl?: string;
  podcastLabel?: string;
  videoUrls?: string[];
  videoLabels?: string[];
  additionalUrls?: string[];
  additionalLabels?: string[];
}

const toScheduleItem = (row: ReadingItem): ScheduleItem => ({
  date: row.date,
  readingDate: row.reading_date ?? undefined,
  location: row.location,
  text: row.text,
  readingPrompt: row.reading_prompt ?? undefined,
  month: row.month,
  isOff: row.is_off,
  offReason: row.off_reason ?? undefined,
  esvUrl: row.esv_url ?? undefined,
  podcastUrl: row.podcast_url ?? undefined,
  podcastLabel: row.podcast_label ?? undefined,
  videoUrls: row.video_urls,
  videoLabels: row.video_labels,
  additionalUrls: row.additional_urls,
  additionalLabels: row.additional_labels,
});

const Reading: React.FC = () => {
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [pageContent, setPageContent] = useState<ReadingPageContent>(defaultPageContent);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let active = true;
    fetchReadingPageContent()
      .then((content) => {
        if (active && content) setPageContent(content);
      })
      .catch(() => {
        /* keep default wording on failure */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetchReadingSchedule()
      .then((rows) => {
        if (!active) return;
        setSchedule(rows.map(toScheduleItem));
        setLoadError(false);
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const monthGroups = schedule.reduce<{ key: string; label: string; items: ScheduleItem[] }[]>(
    (acc, item) => {
      const d = item.readingDate ? new Date(`${item.readingDate}T00:00:00`) : null;
      const key =
        d && !isNaN(d.getTime())
          ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
          : item.month;
      const label =
        d && !isNaN(d.getTime())
          ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          : item.month;
      const existing = acc.find((g) => g.key === key);
      if (existing) existing.items.push(item);
      else acc.push({ key, label, items: [item] });
      return acc;
    },
    []
  );

  const hasLinks = (item: ScheduleItem) =>
    item.podcastUrl || (item.videoUrls?.length ?? 0) > 0 || (item.additionalUrls?.length ?? 0) > 0;

  const getLocation = (item: ScheduleItem): string | null => {
    if (item.isOff) return null;
    return item.location || null;
  };

  return (
    <>
      <SEO
        title="Gospel of John Study | Monday Night Group"
        description="Discovering the person, power, and purpose of Jesus Christ through the Gospel of John."
        image="https://images.pexels.com/photos/159866/books-book-pages-read-literature-159866.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630"
        url="https://mondaynightgroup.com/reading"
      />

      <section
        className="relative h-[50vh] bg-gradient-to-r from-blue-900 to-blue-700 flex items-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/159866/books-book-pages-read-literature-159866.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black to-blue-900/90"></div>
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 drop-shadow-lg">
              {pageContent.header_title}
            </h1>
            <p className="text-xl text-white font-medium drop-shadow-lg">
              {pageContent.header_description}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-12"
            >
              {loading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              )}
              {!loading && loadError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-center">
                  We couldn't load the reading schedule right now. Please refresh the page to try again.
                </div>
              )}
              {!loading && !loadError && schedule.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 text-gray-600 rounded-xl p-6 text-center">
                  The reading schedule will be posted here soon.
                </div>
              )}
              {!loading && !loadError && monthGroups.map(({ key, label, items }) => (
                <div key={key} className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-600 pb-2">
                    {label}
                  </h2>
                  <div className="bg-gray-50 rounded-xl shadow-md overflow-hidden">
                    <div className="divide-y divide-gray-200">
                      {items.map((item, index) => (
                        <div key={index} className="p-6">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:gap-6">
                            <div className="flex-1 min-w-0">
                              <p className="mb-1 whitespace-nowrap">
                                <span className="font-semibold text-blue-600 text-sm">{item.date}</span>
                                {getLocation(item) && (
                                  <>
                                    <MapPin className="inline-block h-3 w-3 mx-1 text-gray-400 align-middle" />
                                    {getLocation(item) === 'Otter Creek Church' ? (
                                      <a
                                        href="https://maps.google.com/?q=3534+West+End+Ave+Nashville+TN+37205"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 text-xs hover:underline align-middle"
                                      >Otter Creek Church</a>
                                    ) : (
                                      <span className="text-xs text-gray-500 align-middle">{getLocation(item)}</span>
                                    )}
                                  </>
                                )}
                              </p>
                              <h3 className="text-xl font-semibold text-gray-900 mb-1">{item.text}</h3>
                              {!item.isOff && item.readingPrompt && (
                                <p className="text-sm text-gray-600 mb-2">{item.readingPrompt}</p>
                              )}
                              {!item.isOff && safeUrl(item.esvUrl) && (
                                <a
                                  href={safeUrl(item.esvUrl)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors mt-1"
                                >
                                  Read {item.text} (ESV)
                                  <ExternalLink className="h-3.5 w-3.5 ml-1.5 flex-shrink-0" />
                                </a>
                              )}
                            </div>
                          {hasLinks(item) && (
                            <div className="grid grid-cols-2 gap-2 mt-3 sm:mt-0 flex-shrink-0 sm:w-56">
                              {safeUrl(item.podcastUrl) && (
                                <a
                                  href={safeUrl(item.podcastUrl)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                                >
                                  {item.podcastLabel || 'BEMA Podcast'}
                                  <ExternalLink className="h-3.5 w-3.5 ml-1.5 flex-shrink-0" />
                                </a>
                              )}
                              {item.videoUrls && item.videoUrls.map((url, vi) => safeUrl(url) && (
                                <a
                                  key={vi}
                                  href={safeUrl(url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                                >
                                  {item.videoLabels?.[vi] || (item.videoUrls!.length > 1 ? `Hamrick Sermon ${vi + 1}` : 'Hamrick Sermon')}
                                  <ExternalLink className="h-3.5 w-3.5 ml-1.5 flex-shrink-0" />
                                </a>
                              ))}
                              {item.additionalUrls && item.additionalUrls.map((url, ai) => {
                                const href = safeUrl(url);
                                if (!href) return null;
                                const customLabel = item.additionalLabels?.[ai];
                                const isBema = url.includes('bemadiscipleship.com');
                                const bemaUrls = item.additionalUrls!.filter(u => u.includes('bemadiscipleship.com'));
                                const bemaIdx = bemaUrls.indexOf(url);
                                const fallback = isBema ? (bemaUrls.length > 1 ? `BEMA Podcast ${bemaIdx + 1}` : 'BEMA Podcast') : 'Additional Resources';
                                return (
                                  <a
                                    key={ai}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors ${isBema ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600'}`}
                                  >
                                    {customLabel || fallback}
                                    <ExternalLink className="h-3.5 w-3.5 ml-1.5 flex-shrink-0" />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <div className="bg-blue-50 rounded-lg p-6 flex items-start space-x-4">
                <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{pageContent.footer_title}</h3>
                  <p className="text-gray-600 whitespace-pre-line">{pageContent.footer_description}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Reading;
